// src/services/assistantService.js
export async function sendAssistantMessage({
  messages,
  jwt,
  onTextChunk,
  onToolCall = () => {},
  onToolResult = () => {},
  onDone,
  onError,
}) {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL_BASE}/assistant/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      onError(new Error(body.message || `Assistant request failed: ${response.status}`));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6).trim();
        if (!payload || payload === "[DONE]") continue;

        let chunk;
        try {
          chunk = JSON.parse(payload);
        } catch (parseError) {
          console.error("assistantService: failed to parse SSE payload", payload, parseError);
          continue;
        }

        // TEMP diagnostics (assistant tool-call investigation): logs which
        // stream events actually reach the browser.
        if (chunk.type !== "text-delta" && chunk.type !== "tool-input-delta") {
          console.debug("[assistant SSE]", chunk.type, chunk.toolName || "", chunk.output || "");
        }

        switch (chunk.type) {
          case "text-delta":
            onTextChunk(chunk.delta);
            break;
          case "tool-input-available":
            onToolCall({
              toolCallId: chunk.toolCallId,
              toolName: chunk.toolName,
              args: chunk.input,
            });
            break;
          case "tool-output-available":
            onToolResult({
              toolCallId: chunk.toolCallId,
              output: chunk.output,
            });
            break;
          case "error":
            onError(new Error(chunk.errorText));
            break;
          case "tool-input-error":
            // The model called a tool with arguments that failed schema
            // validation (e.g. wrong type/format). The AI SDK reports this
            // as a structured stream event, not an HTTP error — surface it
            // instead of silently dropping it, or the user sees the typing
            // indicator vanish with no explanation.
            onError(
              new Error(
                chunk.errorText ||
                  `No pude preparar la acción "${chunk.toolName}" con esos datos. Intenta reformular tu solicitud.`
              )
            );
            break;
          case "tool-output-error":
            onError(new Error(chunk.errorText || "Ocurrió un error al ejecutar la acción."));
            break;
          default:
            // start / text-start / text-end / finish
            // are structural or not needed for the current UI.
            break;
        }
      }
    }
    onDone();
  } catch (error) {
    onError(error);
  }
}

export async function confirmToolAction({ toolName, args, jwt }) {
  const response = await fetch(`${process.env.REACT_APP_API_URL_BASE}/assistant/tools/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ toolName, args }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || `Confirm request failed: ${response.status}`);
  }
  return response.json();
}

export async function resetConversation(jwt) {
  const response = await fetch(`${process.env.REACT_APP_API_URL_BASE}/assistant/conversations/today`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || `Reset request failed: ${response.status}`);
  }
}

export async function fetchTodayConversation(jwt) {
  const response = await fetch(`${process.env.REACT_APP_API_URL_BASE}/assistant/conversations/today`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!response.ok) return { messages: [] };
  return response.json();
}
